import classNames from 'classnames'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TradingViewChart } from '@pages/Trade/Markets/Charts/TradingViewChart'
import {
  readStoredChartTimeframe,
  readStoredChartTimezone,
  writeStoredChartTimeframe,
  writeStoredChartTimezone,
} from '@pages/Trade/Markets/Charts/utils/utils'
import { useFixTradingWebSocketContext } from '@pages/Trade/Portfolio/context/FixTradingWebSocketContext'
import { useCurrentPriceForTrade } from '@pages/Trade/Portfolio/hooks/useCurrentPricesStore'
import { useDxtradeInstruments } from '@pages/Trade/Portfolio/hooks/useDxtradeInstruments'
import { useDxtradeOpenPositions } from '@pages/Trade/Portfolio/hooks/useDxtradeOpenPositions'
import { InsightsTabBody } from '@pages/Trade/Widget/InsightsTabBody'
import { MarketTabBody } from '@pages/Trade/Widget/MarketTabBody'
import { useDxtradeInstrumentDetails } from '@pages/Trade/Widget/hooks/useDxtradeInstrumentDetails'
import { Account } from '@pages/Trade/types'

import { useSnackbar } from '@context/SnackbarContext'

import { ClientApiClient } from '@utils/clientApi'

import { FIX_ORDER_TYPE } from '@model/DxTrade.types'
import type {
  DxTradeExecutableSide,
  FixOrderGroupPayload,
  FixOrderTypeCode,
  FixPositionEffectCode,
  FixSingleOrderPayload,
} from '@model/DxTrade.types'
import type { CurrencyType } from '@model/WalletDto'

import { Box } from '@shared/components/Box'

import { FixContingencyTypes } from '../Portfolio/hooks/useFixTradingWebSocket'
import { sideMapping } from '../Portfolio/utils/mapping'
import { FIX_POSITION_EFFECT, FIX_TIME_IN_FORCE } from '../utils/constants'
import { MarketWidgetBottom } from './MarketWidgetBottom'
import { OrderWidgetBody } from './OrderWidgetBody'
import { OrderWidgetBottom } from './OrderWidgetBottom'
import { OrderWidgetTab, WidgetTop, type OrderWidgetVariant } from './WidgetTop'
import { WidgetTradesTab } from './WidgetTradesTab'

import styles from './Widget.module.scss'

const isOpeningMarketPosition = (
  ordType: FixOrderTypeCode,
  positionEffect?: FixPositionEffectCode
) => ordType === FIX_ORDER_TYPE.MARKET && positionEffect !== FIX_POSITION_EFFECT.CLOSE

export interface WidgetProps {
  asset: string
  accountCode: string
  accountCurrency?: CurrencyType
  initialSide: DxTradeExecutableSide
  initialTab?: OrderWidgetTab
  variant?: OrderWidgetVariant
  onClose: () => void
  onSwitchToOrderFromMarket?: (side: DxTradeExecutableSide) => void
  className?: string
  style?: React.CSSProperties
  selectedAccount: Account
  favoriteSymbols: string[]
  onToggleFavorite: (symbol: string) => void
  api: ClientApiClient
  selectedInstrumentSymbol: string
  onSideChange?: (side: DxTradeExecutableSide) => void
  onTabChange?: (tab: OrderWidgetTab) => void
}

export const Widget = ({
  asset,
  accountCode,

  initialSide,
  initialTab = OrderWidgetTab.Order,
  variant = 'order',
  onClose,
  onSwitchToOrderFromMarket,
  className,
  style,
  selectedAccount,
  favoriteSymbols,
  onToggleFavorite,
  api,
  selectedInstrumentSymbol,
  onSideChange,
  onTabChange,
}: WidgetProps) => {
  const account = `default:${accountCode}`
  const [side, setSide] = useState<DxTradeExecutableSide>(initialSide)
  const [activeTab, setActiveTab] = useState<OrderWidgetTab>(initialTab)
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false)
  const [orderPayload, setOrderPayload] = useState<
    FixSingleOrderPayload | FixOrderGroupPayload | null
  >(null)
  const [chartTimeframe, setChartTimeframe] = useState(() => readStoredChartTimeframe())
  const [chartTimezone, setChartTimezone] = useState(() => readStoredChartTimezone())

  const accountCurrency = selectedAccount.currency?.id
  const handleUserSideChange: React.Dispatch<React.SetStateAction<DxTradeExecutableSide>> =
    useCallback(
      (action: React.SetStateAction<DxTradeExecutableSide>) => {
        setSide((prev) => {
          const newSide = typeof action === 'function' ? action(prev) : action
          if (newSide !== prev) {
            onSideChange?.(newSide)
          }
          return newSide
        })
      },
      [onSideChange]
    )

  const { instruments: instrumentDetails, isLoading: isLoadingDetails } =
    useDxtradeInstrumentDetails(accountCode, asset)

  const { instruments: instrumentsMap, isLoading: isLoadingInstruments } = useDxtradeInstruments(
    accountCode,
    [asset]
  )

  const {
    data: openPositionsData,
    isLoading: isOpenPositionsLoading,
    forceRefresh: refreshOpenPositions,
  } = useDxtradeOpenPositions(accountCode)

  const openPositionsForAsset = useMemo(
    () => (openPositionsData?.positions ?? []).filter((p) => p.symbol === asset),
    [openPositionsData, asset]
  )

  const openPositionsCountForAsset = useMemo(
    () => openPositionsForAsset.length,
    [openPositionsForAsset]
  )
  const hasTradesForAsset = openPositionsCountForAsset > 0

  const instrument = instrumentDetails?.[0]
  const instrumentInfo = instrumentsMap[asset]
  const instrumentDescription = instrumentInfo?.description || ''
  const isInstrumentLoading = isLoadingDetails || isLoadingInstruments
  const priceStep = instrumentInfo?.priceIncrement ?? undefined

  const currentPrice = useCurrentPriceForTrade(asset, side, 'order')
  const sellPrice = useCurrentPriceForTrade(asset, 'SELL', 'order')
  const buyPrice = useCurrentPriceForTrade(asset, 'BUY', 'order')

  const tpslBasePrice = side === 'BUY' ? sellPrice : buyPrice

  const [isCreatingOrder, setIsCreatingOrder] = useState(false)
  const isMountedRef = useRef(true)
  const shouldSwitchToTradesRef = useRef(false)

  const { newOrder, newOrderList } = useFixTradingWebSocketContext()
  const { t } = useTranslation()
  const { addSnackbar } = useSnackbar()

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleOrderSettled = useCallback(() => {
    if (isMountedRef.current) {
      setIsCreatingOrder(false)
    }
  }, [])

  const handleSuccess = useCallback(() => {
    if (hasTradesForAsset) {
      setActiveTab(OrderWidgetTab.Trades)
      onTabChange?.(OrderWidgetTab.Trades)
    } else {
      shouldSwitchToTradesRef.current = true
    }
  }, [hasTradesForAsset, onTabChange])

  const tradingInfo = useMemo(() => {
    if (!instrumentInfo || !instrument) return null

    return {
      lotSize: instrumentInfo.lotSize,
      minOrderSize: instrument.minOrderSize,
      maxOrderSize: instrument.maxOrderSize?.value,
      minOrderSizeIncrement: instrument.minOrderSizeIncrement,
      priceIncrement: instrumentInfo.priceIncrement,
      tradingHours: instrumentInfo.tradingHours,
      marginRate: instrument.marginRate,
    }
  }, [instrumentInfo, instrument])

  const submitSingleOrderViaFix = useCallback(
    (payload: FixSingleOrderPayload) => {
      const fixSide = sideMapping(payload.side ?? null)

      if (!fixSide) {
        addSnackbar.error({ message: t('OrderWidget.Order submit error') })
        return
      }

      const symbol = payload.instrument ?? asset
      const orderQty = payload.quantity

      setIsCreatingOrder(true)

      newOrder(
        {
          account,
          symbol,
          side: fixSide,
          orderQty,
          ordType: payload.ordType,
          positionEffect: payload.positionEffect,
          timeInForce: payload.timeInForce,
          price: payload.price,
          expireDate: payload.expireDate,
        },
        isOpeningMarketPosition(payload.ordType, payload.positionEffect) ? handleSuccess : onClose,
        handleOrderSettled
      )
    },
    [account, addSnackbar, asset, handleOrderSettled, handleSuccess, newOrder, onClose, t]
  )

  const handleOrderPayloadChange = useCallback(
    (payload: FixSingleOrderPayload | FixOrderGroupPayload | null) => {
      setOrderPayload(payload)
    },
    []
  )

  const handleOrderSubmit = useCallback(() => {
    if (!orderPayload) return

    const isOrderGroup = 'orders' in orderPayload && 'contingencyType' in orderPayload

    if (isOrderGroup) {
      const groupPayload = orderPayload as FixOrderGroupPayload

      const [parentOrder] = groupPayload.orders

      if (!parentOrder) return

      const symbol = parentOrder.instrument ?? asset

      const commonTimeInForce = (parentOrder.timeInForce ?? FIX_TIME_IN_FORCE.DAY) as string
      const commonExpireDate = parentOrder.expireDate

      const ordersForList = groupPayload.orders.map((order) => {
        const fixSide = sideMapping(order.side ?? null)

        if (!fixSide) {
          throw new Error('Invalid FIX side for NewOrderList')
        }

        const isClosing = order.positionEffect === FIX_POSITION_EFFECT.CLOSE

        return {
          account,
          symbol,
          side: fixSide,
          orderQty: isClosing ? undefined : order.quantity,
          ordType: order.ordType,
          positionEffect: order.positionEffect,
          timeInForce: commonTimeInForce,
          expireDate: commonExpireDate,
          price: order.price,
        }
      })

      setIsCreatingOrder(true)

      newOrderList(
        {
          contingencyType: FixContingencyTypes.IF_THEN,
          orders: ordersForList,
        },
        isOpeningMarketPosition(parentOrder.ordType, parentOrder.positionEffect)
          ? handleSuccess
          : onClose,
        handleOrderSettled
      )
    } else {
      submitSingleOrderViaFix(orderPayload as FixSingleOrderPayload)
    }
  }, [
    account,
    asset,
    handleOrderSettled,
    handleSuccess,
    orderPayload,
    submitSingleOrderViaFix,
    newOrderList,
    onClose,
  ])

  useEffect(() => {
    shouldSwitchToTradesRef.current = false
    setSide(initialSide)
  }, [asset, initialSide])

  useEffect(() => {
    setActiveTab(initialTab)
  }, [asset, initialTab])

  useEffect(() => {
    if (!shouldSwitchToTradesRef.current) return
    if (!hasTradesForAsset) return
    shouldSwitchToTradesRef.current = false
    setActiveTab(OrderWidgetTab.Trades)
    onTabChange?.(OrderWidgetTab.Trades)
  }, [hasTradesForAsset, onTabChange])

  useEffect(() => {
    if (variant === 'market') {
      return
    }

    if (!isOpenPositionsLoading && !hasTradesForAsset && activeTab === OrderWidgetTab.Trades) {
      setActiveTab(OrderWidgetTab.Order)
      onTabChange?.(OrderWidgetTab.Order)
    }
  }, [activeTab, hasTradesForAsset, isOpenPositionsLoading, onTabChange, variant])

  const handleActiveTabChange = useCallback(
    (nextTab: OrderWidgetTab) => {
      setActiveTab(nextTab)
      onTabChange?.(nextTab)
    },
    [onTabChange]
  )

  return (
    <Box className={classNames(styles.orderWidget, className)} style={style}>
      <WidgetTop
        activeTab={activeTab}
        setActiveTab={handleActiveTabChange}
        asset={asset}
        description={instrumentDescription}
        side={side}
        instrumentCurrency={instrumentInfo?.currency as CurrencyType | undefined}
        priceIncrement={priceStep}
        variant={variant}
        onClose={onClose}
        isFavorite={favoriteSymbols.includes(asset)}
        onToggleFavorite={() => onToggleFavorite(asset)}
        tradesCount={openPositionsCountForAsset}
      />
      {variant !== 'market' && activeTab === OrderWidgetTab.Order && (
        <>
          <OrderWidgetBody
            side={side}
            setSide={handleUserSideChange}
            accountCode={accountCode}
            selectedAccountFreeMargin={selectedAccount.platformOverview.freeMargin}
            instrumentSymbol={asset}
            currentPrice={currentPrice}
            tpslBasePrice={tpslBasePrice}
            onValidityChange={(disabled) => setIsSubmitDisabled(disabled)}
            onOrderPayloadChange={handleOrderPayloadChange}
            instrument={instrument}
            instrumentInfo={instrumentInfo}
            isInstrumentLoading={isInstrumentLoading}
            accountCurrency={accountCurrency}
            openPositions={openPositionsForAsset}
          />
          <OrderWidgetBottom
            side={side}
            currentPrice={currentPrice}
            orderPayload={orderPayload}
            priceStep={priceStep}
            disabled={isSubmitDisabled || !orderPayload || isCreatingOrder}
            onSubmit={handleOrderSubmit}
            isSubmitting={isCreatingOrder}
          />
        </>
      )}

      {activeTab === OrderWidgetTab.Trades && (
        <div className={styles.body}>
          <WidgetTradesTab
            accountCode={accountCode}
            accountCurrency={accountCurrency}
            asset={asset}
            openPositionsData={openPositionsData}
            isOpenPositionsLoading={isOpenPositionsLoading}
            instrumentsMap={instrumentsMap}
            onOpenPositionsRefresh={() => refreshOpenPositions()}
          />
        </div>
      )}
      {activeTab === OrderWidgetTab.Market && (
        <div className={styles.body}>
          <MarketTabBody tradingInfo={tradingInfo} isLoading={isInstrumentLoading} />
        </div>
      )}
      {activeTab === OrderWidgetTab.Chart && (
        <>
          <div className={styles.chartBody}>
            <TradingViewChart
              timeframe={chartTimeframe}
              onTimeframeChange={(value) => {
                setChartTimeframe(value)
                writeStoredChartTimeframe(value)
              }}
              timezone={chartTimezone}
              onTimezoneChange={(value) => {
                setChartTimezone(value)
                writeStoredChartTimezone(value)
              }}
              symbol={selectedInstrumentSymbol}
              account={selectedAccount}
              apiClient={api}
              isMobile
              openPositions={openPositionsData.positions ?? []}
            />
          </div>
          <MarketWidgetBottom
            buyPrice={buyPrice}
            sellPrice={sellPrice}
            priceIncrement={priceStep}
            onBuy={() => onSwitchToOrderFromMarket?.('BUY')}
            onSell={() => onSwitchToOrderFromMarket?.('SELL')}
          />
        </>
      )}
      {activeTab === OrderWidgetTab.News && (
        <div className={styles.body}>
          <InsightsTabBody symbol={asset} />
        </div>
      )}
    </Box>
  )
}
